import { useEffect, useState } from "react";
import { listTransactions, returnTool, exportTransactionsCSV } from "@/lib/firestore";
import { ISSUE } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, ArrowsClockwise, DownloadSimple, FilePdf } from "@phosphor-icons/react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const CONDITIONS_RETURN = ["good", "fair", "poor", "damaged"];

export default function Transactions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [txns, setTxns] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [showReturn, setShowReturn] = useState(null); // txn object
  const [returnForm, setReturnForm] = useState({ condition_on_return: "good", notes: "" });

  const load = async () => {
    const data = await listTransactions(user, { status_filter: statusFilter || undefined });
    setTxns(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const submitReturn = async (e) => {
    e.preventDefault();
    try {
      await returnTool(showReturn.id, returnForm, user);
      toast.success("Tool returned");
      setShowReturn(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const isOverdue = (t) => t.status === "issued" && t.due_date && new Date(t.due_date) < new Date();

  const runExportCSV = async () => {
    try {
      await exportTransactionsCSV(user);
    } catch (e) { toast.error(e.message || "Export failed"); }
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("MTM — Transactions Report", 14, 15);
    doc.setFont("courier", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Generated: ${new Date().toLocaleString()} · ${txns.length} records`, 14, 21);
    autoTable(doc, {
      startY: 26,
      head: [["Tool", "Code", "User", "Qty", "Issued", "Due", "Returned", "Status"]],
      body: txns.map((t) => [
        t.tool_name,
        t.tool_code,
        t.user_name,
        String(t.quantity),
        new Date(t.issue_date).toLocaleDateString(),
        t.due_date ? new Date(t.due_date).toLocaleDateString() : "—",
        t.return_date ? new Date(t.return_date).toLocaleDateString() : "—",
        t.status,
      ]),
      headStyles: { fillColor: [255, 79, 0], textColor: 0, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      styles: { fontSize: 8, cellPadding: 2 },
      margin: { left: 14, right: 14 },
    });
    doc.save("transactions_report.pdf");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-[#FF4F00] tracking-widest mb-2">// TRANSACTIONS</div>
          <h1 className="font-display font-black text-4xl">ISSUE & RECEIVE</h1>
          <div className="text-[#A1A1AA] mt-1 text-sm">
            {isAdmin ? "All active and completed tool transactions." : "Your active checkouts and history."}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]"
          >
            <option value="">All statuses</option>
            <option value="issued">Issued</option>
            <option value="returned">Returned</option>
          </select>
          {isAdmin && (
            <>
              <button
                onClick={runExportCSV}
                data-testid="export-transactions-csv"
                className="brutal-btn border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] px-4 py-2.5 flex items-center gap-2 transition-colors duration-150"
              >
                <DownloadSimple size={16} weight="bold" /> CSV
              </button>
              <button
                onClick={exportPDF}
                data-testid="export-transactions-pdf"
                className="brutal-btn bg-[#FF4F00] text-black hover:bg-[#FF6B2B] px-4 py-2.5 flex items-center gap-2 transition-colors duration-150"
              >
                <FilePdf size={16} weight="bold" /> PDF
              </button>
            </>
          )}
        </div>
      </div>

      {showReturn && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowReturn(null)}>
          <div onClick={(e) => e.stopPropagation()} className="brutal-card p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="font-mono text-xs text-[#FF4F00] tracking-widest">// RECEIVE TOOL</div>
                <h2 className="font-display font-black text-2xl">{showReturn.tool_name}</h2>
                <div className="font-mono text-xs text-[#A1A1AA]">from {showReturn.user_name} · qty {showReturn.quantity}</div>
              </div>
              <button onClick={() => setShowReturn(null)} className="text-[#A1A1AA] hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={submitReturn} className="space-y-3">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1 block">Condition on Return</label>
                <select
                  data-testid={ISSUE.returnCondition}
                  value={returnForm.condition_on_return}
                  onChange={(e) => setReturnForm({ ...returnForm, condition_on_return: e.target.value })}
                  className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]"
                >
                  {CONDITIONS_RETURN.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {returnForm.condition_on_return === "damaged" && (
                  <div className="mt-2 text-xs text-[#FFD700] font-mono">// Damaged: a report will be auto-created for admin review //</div>
                )}
              </div>
              <textarea
                data-testid={ISSUE.returnNotes}
                rows={3}
                placeholder="Notes (optional)"
                value={returnForm.notes}
                onChange={(e) => setReturnForm({ ...returnForm, notes: e.target.value })}
                className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]"
              />
              <button type="submit" data-testid={ISSUE.returnSubmit} className="brutal-btn w-full bg-[#FF4F00] text-black px-4 py-3 hover:bg-[#FF6B2B] transition-colors duration-150">
                Confirm Return →
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="brutal-card overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-[#27272A]">
            <tr className="text-left">
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">Tool</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">User</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">Qty</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">Issued</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">Due</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">Returned</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">Status</th>
              {isAdmin && <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">Actions</th>}
            </tr>
          </thead>
          <tbody className="table-row-alt">
            {txns.length === 0 && (
              <tr>
                <td colSpan={isAdmin ? 8 : 7} className="px-4 py-8 text-center text-[#52525B] font-mono">
                  // no transactions yet //
                </td>
              </tr>
            )}
            {txns.map((t) => (
              <tr key={t.id} className="border-b border-[#1a1a1e] hover:bg-[#141416]">
                <td className="px-4 py-3">
                  <div className="text-sm font-semibold">{t.tool_name}</div>
                  <div className="font-mono text-xs text-[#52525B]">#{t.tool_code}</div>
                </td>
                <td className="px-4 py-3 text-sm">{t.user_name}</td>
                <td className="px-4 py-3 font-mono text-sm">{t.quantity}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#A1A1AA]">{new Date(t.issue_date).toLocaleDateString()}</td>
                <td className={`px-4 py-3 font-mono text-xs ${isOverdue(t) ? "text-[#D32F2F] font-bold" : "text-[#A1A1AA]"}`}>
                  {t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}
                  {isOverdue(t) && <div className="text-[10px] uppercase tracking-widest">Overdue</div>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-[#A1A1AA]">
                  {t.return_date ? new Date(t.return_date).toLocaleDateString() : "—"}
                  {t.condition_on_return && (
                    <div className="text-[10px] uppercase text-[#52525B]">{t.condition_on_return}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                    t.status === "issued" ? "border-[#FF4F00] text-[#FF4F00]" : "border-[#00E676] text-[#00E676]"
                  }`}>{t.status}</span>
                </td>
                {isAdmin && (
                  <td className="px-4 py-3">
                    {t.status === "issued" && (
                      <button
                        data-testid={ISSUE.returnBtn(t.id)}
                        onClick={() => { setReturnForm({ condition_on_return: "good", notes: "" }); setShowReturn(t); }}
                        className="brutal-btn text-xs border border-[#00E676] text-[#00E676] hover:bg-[#00E676] hover:text-black px-3 py-1.5 flex items-center gap-1 transition-colors duration-150"
                      >
                        <ArrowsClockwise size={12} weight="bold" /> Receive
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
